"use client";

import * as AvatarPrimitive from "@radix-ui/react-avatar";
import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Radix-backed avatar with a fallback layer. The fallback only paints
 * after the image network request fails or doesn't resolve within
 * `delayMs`, so operators with slow CDNs don't see an FoUC flash on
 * every render.
 */
export const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn("relative flex h-8 w-8 shrink-0 overflow-hidden rounded-full", className)}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

export const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full object-cover", className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

export const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    delayMs={300}
    className={cn(
      "flex h-full w-full items-center justify-center bg-wx-bg-elevated-2 text-xs font-medium text-wx-fg-muted",
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

interface AvatarStackProps {
  avatars: { src: string; alt: string }[];
  /** Max avatars to render — extras collapse into a `+N` badge. */
  max?: number;
  size?: "sm" | "md";
  /** Ring colour — matches the surface the stack sits on, so the
   *  -space-x overlap reads as separation, not muddle. */
  ringClass?: string;
  /** Outer-wrapper class — caller-controlled spacing/positioning. */
  className?: string;
}

/**
 * Stack composition over `Avatar` — header + Home-tab CTA both render
 * the same affordance. `max` capped at 3 by default; the +N badge
 * uses the same dimensions so the stack width is predictable.
 */
export function AvatarStack({
  avatars,
  max = 3,
  size = "md",
  ringClass = "ring-wx-bg",
  className,
}: AvatarStackProps) {
  const visible = avatars.slice(0, max);
  const overflow = avatars.length - visible.length;
  const dim = size === "sm" ? "h-7 w-7" : "h-8 w-8";

  return (
    <div className={cn("flex -space-x-2", className)}>
      {visible.map((avatar) => (
        <Avatar key={avatar.src} className={cn(dim, "ring-2", ringClass)}>
          <AvatarImage src={avatar.src} alt={avatar.alt} />
          <AvatarFallback>{initials(avatar.alt)}</AvatarFallback>
        </Avatar>
      ))}
      {overflow > 0 && (
        <Avatar className={cn(dim, "ring-2", ringClass)}>
          <AvatarFallback>+{overflow}</AvatarFallback>
        </Avatar>
      )}
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}
